/**
 * WaveProvider — ONE interface for "the sea's waves", shared by everything that must agree about the surface
 * (shader gaps R5, 2026-09-30): the material's displacement, slope, Jacobian and previous position (motion vectors),
 * the foam memories, the projected caustics, and the gameplay height queries (riders, swimmers, fish, the waterline).
 *
 * TWO IMPLEMENTATIONS.
 *  - `GerstnerWaveProvider` — a PURE WRAPPER over the existing Gerstner set (GerstnerWaves.ts + tsl-gerstner.ts). Its
 *    CPU queries are the SAME calls the owners made before, with the same arguments in the same order (pinned
 *    bit-for-bit by tests/unit/wave-provider.test.ts); consumers keep their old Gerstner code paths for it, so the
 *    Gerstner sea draws exactly as it did.
 *  - `FftWaveProvider` (fft/FftWaveProvider.ts) — one Tessendorf patch on TSL compute (WebGPU only), with a CPU mirror of
 *    the band the mesh displaces for the height queries (queryLatencyS 0: no readback — the same modes at the same t).
 *
 * COORDINATES. The CPU queries take ABSOLUTE world XZ (the rows' frame). The owner calls `setRenderShift(sx, sz)` with
 * the FP64 rebase shift (`scene.position.xz`) before a query or once per frame: the Gerstner phase lives in RENDER space
 * (x + sx — what the shaders' `uWorldOffset` carries), the FFT patch in absolute space mod P (seamless across rebases).
 *
 * THE QUERIES. `sampleLiveHeight` is what gameplay stands on (Gerstner: the FULL set, as it always was — unless the
 * owner opts into `liveOnDrawn`; FFT: the drawn surface); `sampleDrawnHeight` is the geometry the mesh displaces (THE
 * DRAWN SEA — the vertex-filtered set / band). With a drawn grid (`setDrawnGrid`) both providers answer the displaced
 * TRIANGLE of the owner's mesh over the point (`drawnSurface.solveDrawnTriangle` — THE RIDER LAW), never the continuous
 * field, whose chord sag over an 8 m triangle is decimetres on a swell.
 * Both take an optional per-point (Gerstner) or per-VERTEX (FFT, with a drawn grid) scale — the shoal × distance fade the
 * vertex stage multiplies the displacement by.
 */

import {
  filterVertexWaves,
  maxAmplitude,
  sampleDisplacement,
  sampleHeight,
  sampleJacobian,
  sampleNormal,
  IDENTITY_MODULATION,
  type GerstnerWave,
  type WaveModulation,
} from './GerstnerWaves.js';
import { makeTslGerstner, type TslGerstner, type TslWaveModulation } from './tsl-gerstner.js';
import { totalSlopeVariance } from './waterLook.js';
import { solveDrawnTriangle, type DrawnGrid } from './drawnSurface.js';

/** The three evaluator sets a surface material needs (each has the TslGerstner shape; only `displacement` is read from the vertex sets). */
export interface TslWaveField {
  /** Fragment stage: normal, normalFiltered, jacobian, displacement (the crest height). */
  surface: TslGerstner;
  /** Vertex stage: what the mesh displaces. */
  vertex: TslGerstner;
  /** Vertex stage one frame back — the positionPrevious LAW. */
  vertexPrevious: TslGerstner;
}

/** A per-point scale on the displacement (the vertex stage's shoal × distance fade), absolute XZ. */
export type WaveScaleAt = (x: number, z: number) => number;

export interface WaveProvider {
  readonly isWaveProvider: true;
  readonly kind: 'gerstner' | 'fft';
  /** Gerstner: the full wave set the fragment evaluates; FFT: null. */
  readonly waves: GerstnerWave[] | null;
  /** How late a CPU height answer is relative to the drawn surface (s). 0 = the same instant. */
  readonly queryLatencyS: number;
  /** A bound on |CPU drawn height − the GPU's drawn surface| (m) at the current sea state — see each implementation. */
  heightErrorBoundM(): number;
  /** The FP64 rebase shift (scene.position.xz) — absolute + shift = render space. */
  setRenderShift(x: number, z: number): void;
  sampleDisplacement(x: number, z: number, t: number, mod?: Readonly<WaveModulation>): { dx: number; dy: number; dz: number };
  sampleLiveHeight(x: number, z: number, t: number, mod?: Readonly<WaveModulation>, scaleAt?: WaveScaleAt): number;
  sampleDrawnHeight(x: number, z: number, t: number, mod?: Readonly<WaveModulation>, scaleAt?: WaveScaleAt): number;
  sampleNormal(x: number, z: number, t: number, mod?: Readonly<WaveModulation>): { nx: number; ny: number; nz: number };
  /** Set (or clear) the owner's drawn mesh so the drawn query solves its displaced triangles (THE RIDER LAW). */
  setDrawnGrid(g: DrawnGrid | null): void;
  sampleJacobian(x: number, z: number, t: number, mod?: Readonly<WaveModulation>): number;
  /** Crest scale for the crest-foam band / the scattering's crest height (m at amplitude scale 1). */
  maxAmplitude(): number;
  /** Mean-square slope of everything the surface carries (m²/m², amplitude scale 1) — what a flat far sheet folds into α². */
  totalSlopeVariance(): number;
  /**
   * The TSL evaluators on the material's clock (`uTime`), its frame delta (`uFrameDt`, for the previous position) and
   * sea-state nodes (`mod`). `vertexWavelengthCutoff` is the mesh's Nyquist cut (Gerstner filters its set by it).
   */
  tsl(uTime: unknown, uFrameDt: unknown, mod: TslWaveModulation | null, vertexWavelengthCutoff: number): TslWaveField;
  /** Per frame (the FFT dispatches its compute; Gerstner has nothing to do). */
  update(time: number, dt: number, mod?: Readonly<WaveModulation>): void;
  dispose(): void;
}

export function isWaveProvider(x: unknown): x is WaveProvider {
  return !!x && typeof x === 'object' && (x as { isWaveProvider?: unknown }).isWaveProvider === true;
}

export interface GerstnerWaveProviderOptions {
  /** The mesh's vertex Nyquist cut (m) — the drawn set is `filterVertexWaves(waves, cutoff)`. 0 = every wave. */
  vertexWavelengthCutoff?: number;
  /** The vertex spacing (m) the drawn surface is interpolated over — only for `heightErrorBoundM`. Default 8. */
  vertexSpacing?: number;
  /**
   * THE RIDER LAW (2026-09-30): once a drawn grid is set, gameplay stands on the DRAWN surface — `sampleLiveHeight`
   * answers the displaced triangle and `sampleNormal` the displaced (vertex) set, the FFT provider's rule. Default
   * false: the pure wrapper, every query the old call.
   */
  liveOnDrawn?: boolean;
}

/**
 * The float32 phase budget of the GPU's own evaluation (rad): a vertex's phase is w·x + φ·t with render-space x up to a
 * rebase and t up to the water clock's 2π·1000 s wrap — ~1.5e4 rad, where a float32 step is 1e-3.
 */
const GPU_PHASE_EPS = 2e-3;

/**
 * The Gerstner set behind the provider interface — a PURE wrapper: every query is the GerstnerWaves call the owners made
 * before (absolute + shift = the render-space coordinate the shaders evaluate at).
 */
export class GerstnerWaveProvider implements WaveProvider {
  readonly isWaveProvider = true as const;
  readonly kind = 'gerstner' as const;
  readonly queryLatencyS = 0;
  readonly vertexWaves: GerstnerWave[];
  private shiftX = 0;
  private shiftZ = 0;
  private readonly spacing: number;
  private readonly liveOnDrawn: boolean;
  private drawn: DrawnGrid | null = null;

  constructor(readonly waves: GerstnerWave[], opts: GerstnerWaveProviderOptions = {}) {
    this.vertexWaves = filterVertexWaves(waves, opts.vertexWavelengthCutoff ?? 0);
    this.spacing = opts.vertexSpacing ?? 8;
    this.liveOnDrawn = opts.liveOnDrawn === true;
  }

  setDrawnGrid(g: DrawnGrid | null): void {
    const d = this.drawn;
    if (g && d && g.originX === d.originX && g.originZ === d.originZ && g.spacing === d.spacing && g.cells === d.cells) return;
    this.drawn = g ? { ...g } : null;
  }

  /** The owner's drawn mesh, when set (probes). */
  get drawnGrid(): Readonly<DrawnGrid> | null { return this.drawn; }
  /** Whether gameplay's height and slope read the drawn surface (`liveOnDrawn` with a grid set). */
  get ridesDrawn(): boolean { return this.liveOnDrawn && this.drawn !== null; }

  setRenderShift(x: number, z: number): void { this.shiftX = x; this.shiftZ = z; }

  /**
   * The drawn query evaluates the continuous vertex set at the point; the mesh interpolates it linearly between
   * vertices, so the two differ by at most the chord sag over a triangle's longest edge (the diagonal s·√2):
   * Σ A·(1 − cos(k·s·√2 / 2)) over the drawn waves. (The live query adds the undrawn short waves on top — by design.)
   */
  heightErrorBoundM(ampScale = 1): number {
    let b = 0;
    if (this.drawn) {
      // The triangle solve is the drawn mesh: what remains is the GPU's float32 phase and a millimetre of storage/shoal.
      for (const w of this.vertexWaves) b += w.amplitude * ampScale * GPU_PHASE_EPS;
      return b + 0.001;
    }
    for (const w of this.vertexWaves) b += w.amplitude * ampScale * (1 - Math.cos(Math.min(Math.PI, w.w * this.spacing * Math.SQRT1_2)));
    return b;
  }

  sampleDisplacement(x: number, z: number, t: number, mod: Readonly<WaveModulation> = IDENTITY_MODULATION): { dx: number; dy: number; dz: number } {
    return sampleDisplacement(this.waves, x + this.shiftX, z + this.shiftZ, t, mod);
  }

  sampleLiveHeight(x: number, z: number, t: number, mod: Readonly<WaveModulation> = IDENTITY_MODULATION, scaleAt?: WaveScaleAt): number {
    if (this.liveOnDrawn && this.drawn) return this.sampleDrawnHeight(x, z, t, mod, scaleAt);
    const h = sampleHeight(this.waves, x + this.shiftX, z + this.shiftZ, t, mod);
    return scaleAt ? h * scaleAt(x, z) : h;
  }

  sampleDrawnHeight(x: number, z: number, t: number, mod: Readonly<WaveModulation> = IDENTITY_MODULATION, scaleAt?: WaveScaleAt): number {
    const g = this.drawn;
    if (!g) {
      const h = sampleHeight(this.vertexWaves, x + this.shiftX, z + this.shiftZ, t, mod);
      return scaleAt ? h * scaleAt(x, z) : h;
    }
    // THE RIDER LAW: the displaced triangle over (x, z) — each vertex displaced by the vertex set at ITS render-space
    // position and scaled as the vertex stage scales it (the shoal × the distance fade), exactly what the mesh draws.
    const sp = g.spacing, sx = this.shiftX, sz = this.shiftZ, vw = this.vertexWaves;
    return solveDrawnTriangle(x, z, g, (i, j, out) => {
      const bx = g.originX + i * sp, bz = g.originZ + j * sp;
      const d = sampleDisplacement(vw, bx + sx, bz + sz, t, mod);
      const k = scaleAt ? scaleAt(bx, bz) : 1;
      out[0] = bx + k * d.dx;
      out[1] = k * d.dy;
      out[2] = bz + k * d.dz;
    });
  }

  sampleNormal(x: number, z: number, t: number, mod: Readonly<WaveModulation> = IDENTITY_MODULATION): { nx: number; ny: number; nz: number } {
    // A rider tilts with the surface it stands on: the displaced set (the full set's short waves live in the pixel normal).
    return sampleNormal(this.liveOnDrawn && this.drawn ? this.vertexWaves : this.waves, x + this.shiftX, z + this.shiftZ, t, mod);
  }

  sampleJacobian(x: number, z: number, t: number, mod: Readonly<WaveModulation> = IDENTITY_MODULATION): number {
    return sampleJacobian(this.waves, x + this.shiftX, z + this.shiftZ, t, mod);
  }

  maxAmplitude(): number { return maxAmplitude(this.waves); }
  totalSlopeVariance(): number { return totalSlopeVariance(this.waves); }

  /** Exactly the evaluators RealisticWaterMaterial builds for a wave set (it keeps building them itself for this kind). */
  tsl(uTime: unknown, uFrameDt: unknown, mod: TslWaveModulation | null, vertexWavelengthCutoff: number): TslWaveField {
    const t = uTime as { mul(v: unknown): unknown; sub(v: unknown): unknown };
    const surface = makeTslGerstner(this.waves, t as never, mod ?? undefined);
    const vw = filterVertexWaves(this.waves, vertexWavelengthCutoff);
    const vertex = vw.length === this.waves.length ? surface : makeTslGerstner(vw, t as never, mod ?? undefined);
    const vertexPrevious = makeTslGerstner(vw, t.sub(uFrameDt) as never, mod ?? undefined);
    return { surface, vertex, vertexPrevious };
  }

  update(): void { /* the Gerstner set is analytic in the shader's clock */ }
  dispose(): void { /* nothing owned */ }
}
